import { CheckboxItem } from 'components/core/checkbox-group';
import { RadioButtonItem } from 'components/core/radio-button-group';

import { MapsFiguresOverviewFieldsFragment } from 'api/graphql-types';
import { useAppSelector } from 'redux/store';
import { IFilters } from 'components/filter-sidebar';
import { setModalOpen, setModalTitle, setModalType } from 'redux/stores/maps-figures';
import { useDispatch } from 'react-redux';
import { GetKindOptions } from './constants';
import { useKind, useTheme } from 'utils/hooks/queries';

const useMapsFiguresFilters = (
	module: MapsFiguresOverviewFieldsFragment,
	onChange: (themeIds: string[], kindIds: string[]) => void
): IFilters => {
	const maxItemsAmount = 4;
	const dispatch = useDispatch();
	const kinds = GetKindOptions();
	const { selectedThemes, setSelectedThemesParam } = useTheme();
	const { selectedKinds, setSelectedKindsParam } = useKind(kinds);

	const { themes, totalThemes } = useAppSelector((state) => state.themes);

	const themesFilter = {
		id: 'selectedThemes',
		type: 'checkbox',
		title: module.themeFilterText || '',
		onChange: (items: CheckboxItem[], active: boolean): void => {
			if (active) {
				const mergedThemes = [...selectedThemes, ...items];
				setSelectedThemesParam(mergedThemes.length > 0 ? mergedThemes : null);
				onChange(
					mergedThemes.map((item) => String(item.id)),
					selectedKinds.map((item) => String(item.id))
				);
			} else {
				const itemIds = new Set(items.map((newItem) => newItem.id));
				const newThemes = selectedThemes.filter((selectedItem) => !itemIds.has(selectedItem.id));
				setSelectedThemesParam(newThemes.length > 0 ? newThemes : null);
				onChange(
					newThemes.map((item) => String(item.id)),
					selectedKinds.map((item) => String(item.id))
				);
			}
		},
		value: selectedThemes,
		showMore: module.showAllThemesText,
		options: themes?.map((theme) => ({
			id: theme.documentId,
			label: theme?.attributes?.title
		})) as CheckboxItem[],
		handleShowMore:
			totalThemes && totalThemes > maxItemsAmount
				? () => {
						dispatch(setModalTitle(module.themeFilterText));
						dispatch(setModalType('themes'));
						dispatch(setModalOpen(true));
				  }
				: undefined
	};

	const kindFilter = kinds.length > 0 && {
		id: 'kind',
		type: 'radio',
		title: module.kindFilterText,
		onChange: (item: RadioButtonItem): void => {
			setSelectedKindsParam(item);
			onChange(
				selectedThemes.map((theme) => String(theme.id)),
				item.id ? [String(item.id.split('-')[1])] : []
			);
		},
		value: { id: `kind-${selectedKinds[0]?.id}`, label: selectedKinds[0]?.label },
		options: kinds.map((item) => ({
			id: `kind-${item.id}`,
			label: item?.name
		})) as RadioButtonItem[]
	};

	return [kindFilter, themesFilter].filter((item) => item !== false) as IFilters;
};

export default useMapsFiguresFilters;
